import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import * as THREE from 'three';
import useEditorStore from './useEditorStore';
import useFinalizationDataStore from './useFinalizationDataStore';

const DEFAULT_HEIGHT_VALUE = 5;
const DEFAULT_DEFLECT_VALUE = 2;
const DEFAULT_FALLOFF_HEIGHT_VALUE = 4;
const DEFAULT_FALLOFF_DEFLECT_VALUE = 1;
const DEFAULT_DEFLECT_BLEND_RATIO = 0.75; // 75% of region keeps full deflect value, 25% blends to 0

const useFlareStore = create(
  devtools((set, get) => ({
    // Flare multislider state
    flareValues: [20, 40, 60, 80, 100], // Initial segment positions
    segmentNames: ['purple', 'yellow', 'orange', 'blue', 'violet'], // Color names for segments

    // Per-segment height and deflect values
    segmentHeightValues: [DEFAULT_HEIGHT_VALUE, DEFAULT_HEIGHT_VALUE, DEFAULT_HEIGHT_VALUE, DEFAULT_HEIGHT_VALUE, DEFAULT_HEIGHT_VALUE], // Height values for each segment in mm
    segmentDeflectValues: [DEFAULT_DEFLECT_VALUE, DEFAULT_DEFLECT_VALUE, DEFAULT_DEFLECT_VALUE, DEFAULT_DEFLECT_VALUE, DEFAULT_DEFLECT_VALUE], // Deflect values for each segment in mm
    segmentFalloffHeightValues: [DEFAULT_FALLOFF_HEIGHT_VALUE, DEFAULT_FALLOFF_HEIGHT_VALUE, DEFAULT_FALLOFF_HEIGHT_VALUE, DEFAULT_FALLOFF_HEIGHT_VALUE, DEFAULT_FALLOFF_HEIGHT_VALUE], // Falloff height values for each segment in mm
    segmentFalloffDeflectValues: [DEFAULT_FALLOFF_DEFLECT_VALUE, DEFAULT_FALLOFF_DEFLECT_VALUE, DEFAULT_FALLOFF_DEFLECT_VALUE, DEFAULT_FALLOFF_DEFLECT_VALUE, DEFAULT_FALLOFF_DEFLECT_VALUE], // Falloff deflect values for each segment in mm

    selectedSegmentIndex: 0, // Which segment is currently selected (0-4)

    // Deflect blending configuration
    deflectBlendRatio: DEFAULT_DEFLECT_BLEND_RATIO, // Ratio of region (0..1) that keeps full deflect value before blending to 0

    // Angular blending configuration (horizontal/circumferential blending between segments)
    angularBlendStartPercent: 25, // Percentage of segment span where ramp-up completes (0-100)
    angularBlendEndPercent: 75,   // Percentage of segment span where ramp-down starts (0-100)

    // Handler state
    enabled: false,
    isFlareApplied: false,
    isFlareAppliedFromDraft: false,
    previewMesh: null,
    baseGeometry: null, // Store original geometry for color restoration

    // Configuration for overlay visualization (resolution-independent)
    overlayConfig: {
      surfaceOffset: 0.05,         // Offset from surface to prevent z-fighting
      selectedBrightness: 1.2,       // Brightness multiplier for selected segment
      unselectedBrightness: 0.7,     // Brightness multiplier for unselected segments
      edgeSmoothness: [0.45, 0.55],  // Smoothstep range for edge band mode
      alphaMode: 'fill',             // 'fill' shows full influence height, 'band' shows only edge
      useDistanceField: true,        // Enable per-fragment distance calculation for smooth gradients
      featherAmount: 0.05,           // Small edge softness; main transition controlled by blendStartRatio
      influenceThreshold: 0.02,      // Keep interior solid; threshold only trims very faint areas
      blendStartRatio: 1          // NEW: start of blending as fraction of height (0..1)
    },

    // Configuration for boundary smoothing (eliminates sharp edges)
    smoothingConfig: {
      enabled: true,                 // Enable/disable boundary smoothing
      boundaryHeightPercent: 0.15,   // Bottom 15% of segment height is boundary zone
      falloffPercent: 0.10,          // 10% additional falloff for smooth transition
      iterations: 4,                 // Number of smoothing iterations
      strength: 0.6,                 // Smoothing strength per iteration (0-1)
      // Angular boundary smoothing (eliminates sharp edges where segments meet)
      enableAngularSmoothing: true,         // Enable cross-segment smoothing
      angularBlendZoneDegrees: 15,           // 15° transition zone at segment boundaries
      angularSmoothingIterations: 4,        // Smoothing iterations for angular boundaries
      angularSmoothingStrength: 0.6,        // Smoothing strength for angular boundaries
      // Transition zone smoothing (eliminates sharp normal discontinuities at flare boundary)
      enableTransitionSmoothing: true,      // Enable flare-to-base transition smoothing
      transitionSmoothingIterations: 5,     // Smoothing iterations for transition zone
      transitionSmoothingStrength: 0.7      // Smoothing strength for transition zone (0-1)
    },

    // Actions
    setFlareValues: (values) => set({ flareValues: values }),
    
    setSegmentNames: (names) => set({ segmentNames: names }),
    
    setSelectedSegmentIndex: (index) => set({ selectedSegmentIndex: index }),

    setDeflectBlendRatio: (ratio) => set({ deflectBlendRatio: Math.max(0, Math.min(1, ratio)) }),

    setAngularBlendStartPercent: (percent) => set({ angularBlendStartPercent: Math.max(0, Math.min(100, percent)) }),

    setAngularBlendEndPercent: (percent) => set({ angularBlendEndPercent: Math.max(0, Math.min(100, percent)) }),

    setSegmentHeightValue: (segmentIndex, height) => set((state) => {
      const newHeightValues = [...state.segmentHeightValues];
      newHeightValues[segmentIndex] = height;
      return { segmentHeightValues: newHeightValues };
    }),
    
    setSegmentDeflectValue: (segmentIndex, deflect) => set((state) => {
      const newDeflectValues = [...state.segmentDeflectValues];
      newDeflectValues[segmentIndex] = deflect;
      return { segmentDeflectValues: newDeflectValues };
    }),
    
    setSegmentFalloffHeightValue: (segmentIndex, falloffHeight) => set((state) => {
      const newFalloffHeightValues = [...state.segmentFalloffHeightValues];
      newFalloffHeightValues[segmentIndex] = falloffHeight;
      return { segmentFalloffHeightValues: newFalloffHeightValues };
    }),

    setSegmentFalloffDeflectValue: (segmentIndex, falloffDeflect) => set((state) => {
      const newFalloffDeflectValues = [...state.segmentFalloffDeflectValues];
      newFalloffDeflectValues[segmentIndex] = falloffDeflect;
      return { segmentFalloffDeflectValues: newFalloffDeflectValues };
    }),
    
    // Legacy actions for backward compatibility (operate on selected segment)
    setHeightValue: (height) => set((state) => {
      const newHeightValues = [...state.segmentHeightValues];
      newHeightValues[state.selectedSegmentIndex] = height;
      return { segmentHeightValues: newHeightValues };
    }),
    
    setDeflectValue: (deflect) => set((state) => {
      const newDeflectValues = [...state.segmentDeflectValues];
      newDeflectValues[state.selectedSegmentIndex] = deflect;
      return { segmentDeflectValues: newDeflectValues };
    }),
    
    setFalloffHeightValue: (falloffHeight) => set((state) => {
      const newFalloffHeightValues = [...state.segmentFalloffHeightValues];
      newFalloffHeightValues[state.selectedSegmentIndex] = falloffHeight;
      return { segmentFalloffHeightValues: newFalloffHeightValues };
    }),

    setFalloffDeflectValue: (falloffDeflect) => set((state) => {
      const newFalloffDeflectValues = [...state.segmentFalloffDeflectValues];
      newFalloffDeflectValues[state.selectedSegmentIndex] = falloffDeflect;
      return { segmentFalloffDeflectValues: newFalloffDeflectValues };
    }),
    
    // Getters for current selected segment values
    getSelectedHeightValue: () => {
      const { segmentHeightValues, selectedSegmentIndex } = get();
      return segmentHeightValues[selectedSegmentIndex] ?? 1.0;
    },

    getSelectedDeflectValue: () => {
      const { segmentDeflectValues, selectedSegmentIndex } = get();
      return segmentDeflectValues[selectedSegmentIndex] ?? 8.0;
    },

    getSelectedFalloffHeightValue: () => {
      const { segmentFalloffHeightValues, selectedSegmentIndex } = get();
      return segmentFalloffHeightValues[selectedSegmentIndex] ?? 5.0;
    },

    getSelectedFalloffDeflectValue: () => {
      const { segmentFalloffDeflectValues, selectedSegmentIndex } = get();
      return segmentFalloffDeflectValues[selectedSegmentIndex] ?? 5.0;
    },

    getSelectedSegmentName: () => {
      const { segmentNames, selectedSegmentIndex } = get();
      return segmentNames[selectedSegmentIndex] || 'purple';
    },

    // Helper to get segment percentages
    getSegmentPercentages: () => {
      const { flareValues } = get();
      return flareValues.map((value, index) => {
        const prevIndex = (index - 1 + flareValues.length) % flareValues.length;
        const segment = (value - flareValues[prevIndex] + 100) % 100;
        return Math.max(segment, 5); // Ensure minimum 5% per segment
      });
    },

    // Handler actions
    setEnabled: (enabled) => set({ enabled }),
    setFlareApplied: (applied) => set({ isFlareApplied: applied }),
    setFlareAppliedFromDraft: (fromDraft) => set({ isFlareAppliedFromDraft: fromDraft }),
    setPreviewMesh: (mesh) => set({ previewMesh: mesh }),
    setBaseGeometry: (geometry) => set({ baseGeometry: geometry }),

    // Command pattern for undo/redo
    currentFlareCommand: null,
    setCurrentFlareCommand: (command) => set({ currentFlareCommand: command }),

    createFlareCommand: () => {
      const {
        previewMesh,
        baseGeometry,
        flareValues,
        segmentHeightValues,
        segmentDeflectValues,
        segmentFalloffHeightValues,
        segmentFalloffDeflectValues,
        deflectBlendRatio
      } = get();
      const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();
      const originalMaterial = sceneRefs.shadedMesh?.material;

      if (!previewMesh) return null;

      const originalMesh = sceneRefs.shadedMesh;
      const originalGeometry = originalMesh.geometry;

      // Clone geometry from preview (has flare deformations + segment visualization colors)
      const cloneGeom = new THREE.BufferGeometry().copy(previewMesh.geometry);

      // Restore original vertex colors from base geometry
      if (baseGeometry?.attributes.color) {
        // Clone the original color attribute and apply it to the new geometry
        const originalColors = baseGeometry.attributes.color.clone();
        cloneGeom.setAttribute('color', originalColors);
      } else if (cloneGeom.attributes.color) {
        // If original had no colors, remove the segment visualization colors
        cloneGeom.deleteAttribute('color');
      }

      // Store flare configuration for finalization
      const flareConfig = {
        flareValues: [...flareValues],
        segmentHeightValues: [...segmentHeightValues],
        segmentDeflectValues: [...segmentDeflectValues],
        segmentFalloffHeightValues: [...segmentFalloffHeightValues],
        segmentFalloffDeflectValues: [...segmentFalloffDeflectValues],
        deflectBlendRatio
      };

      return {
        execute: () => {
          // Remove preview visual
          const latest = get().previewMesh;
          if (latest && latest.isMesh) {
            latest.parent?.remove(latest);
            latest.geometry?.dispose?.();
            latest.material?.dispose?.();
          }

          // Replace shadedMesh with flared geometry
          originalMesh.geometry.dispose();
          originalMesh.geometry = cloneGeom;
          originalMesh.geometry.computeVertexNormals();
          originalMesh.geometry.computeBoundingSphere();
          replaceShadedMesh(originalMesh);

          // Save to finalization store
          useFinalizationDataStore.getState().saveFlare(flareConfig);

          set({
            isFlareApplied: true,
            previewMesh: null,
            baseGeometry: null
          });
        },
        undo: () => {
          // Restore original geometry
          originalMesh.geometry.dispose();
          originalMesh.geometry = originalGeometry;
          originalMesh.geometry.computeVertexNormals();
          originalMesh.geometry.computeBoundingSphere();
          replaceShadedMesh(originalMesh);

          set({
            isFlareApplied: false,
            previewMesh: null,
            baseGeometry: null
          });
        }
      };
    },

    // Overlay configuration updaters (so UI can control bending start etc.)
    setOverlayConfig: (partial) => set((state) => ({ overlayConfig: { ...state.overlayConfig, ...(partial || {}) } })),
    setBlendStartRatio: (ratio) => set((state) => ({ overlayConfig: { ...state.overlayConfig, blendStartRatio: Math.max(0, Math.min(1, ratio)) } })),

    // Reset all height and deflect values to 0
    resetAllValues: () => set({
      segmentHeightValues: [0, 0, 0, 0, 0],
      segmentDeflectValues: [0, 0, 0, 0, 0],
      segmentFalloffHeightValues: [0, 0, 0, 0, 0],
      segmentFalloffDeflectValues: [0, 0, 0, 0, 0]
    }),

    // Reset to default values
    reset: () => set({
      flareValues: [20, 40, 60, 80, 100],
      segmentNames: ['purple', 'yellow', 'orange', 'blue', 'violet'],
      segmentHeightValues: [1.0, 1.5, 2.0, 1.2, 0.8],
      segmentDeflectValues: [8.0, 6.5, 7.2, 9.1, 5.3],
      segmentFalloffHeightValues: [5.0, 5.0, 5.0, 5.0, 5.0],
      segmentFalloffDeflectValues: [5.0, 5.0, 5.0, 5.0, 5.0],
      selectedSegmentIndex: 0,
      deflectBlendRatio: DEFAULT_DEFLECT_BLEND_RATIO,
      angularBlendStartPercent: 25,
      angularBlendEndPercent: 75,
      enabled: false,
      isFlareApplied: false,
      isFlareAppliedFromDraft: false,
      previewMesh: null,
      baseGeometry: null,
      overlayConfig: {
        surfaceOffset: 0.0006,
        selectedBrightness: 1.2,
        unselectedBrightness: 0.7,
        edgeSmoothness: [0.45, 0.55],
        alphaMode: 'fill',
        useDistanceField: true,
        featherAmount: 0.05,
        influenceThreshold: 0.02,
        blendStartRatio: 0.85
      },
      smoothingConfig: {
        enabled: true,
        boundaryHeightPercent: 0.15,
        falloffPercent: 0.10,
        iterations: 4,
        strength: 0.6,
        enableAngularSmoothing: true,
        angularBlendZoneDegrees: 5,
        angularSmoothingIterations: 4,
        angularSmoothingStrength: 0.6
      }
    })
  }), {
    name: 'flare-store'
  })
);

export default useFlareStore;